import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, CreditCard, Files, FilePlus, Loader2, Package, ReceiptText, UserPlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  getCollectionsDashboard,
  getSettings,
  listInvoices,
  type CollectionCustomerSummary,
} from '../api/client';
import { useModules } from '../context/ModulesContext';

const HOME_COLLECTION_CUSTOMER_LIMIT = 10;

function formatApiDate(date: Date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}${month}${year}`;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: 0,
  }).format(value);
}

export default function Home() {
  const { t } = useTranslation();
  const { isModuleEnabled, loading: modulesLoading } = useModules();
  const [paymentLinksEnabled, setPaymentLinksEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [openBalance, setOpenBalance] = useState<number | null>(null);
  const [todayInvoiceCount, setTodayInvoiceCount] = useState<number | null>(null);
  const [collectionCustomers, setCollectionCustomers] = useState<CollectionCustomerSummary[]>([]);
  const [collectionsAvailable, setCollectionsAvailable] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);

  const collectionsEnabled = !modulesLoading && isModuleEnabled('collections');
  const analyticsEnabled = !modulesLoading && isModuleEnabled('analytics');

  const shortcuts = useMemo(() => {
    const items: { to: string; icon: LucideIcon; label: string }[] = [
      { to: '/documents', icon: Files, label: t('home.shortcuts.documents') },
      { to: '/customers', icon: UserPlus, label: t('home.shortcuts.customers') },
      { to: '/items', icon: Package, label: t('home.shortcuts.items') },
    ];
    if (analyticsEnabled) {
      items.push({ to: '/reports', icon: BarChart3, label: t('home.shortcuts.businessAnalysis') });
    }
    if (collectionsEnabled) {
      items.push({ to: '/collections', icon: ReceiptText, label: t('home.shortcuts.collections') });
    }
    return items;
  }, [analyticsEnabled, collectionsEnabled, t]);

  useEffect(() => {
    let active = true;
    setSettingsLoaded(false);
    getSettings()
      .then((res) => {
        const data = res.data?.settings ?? res.data ?? {};
        if (active) setPaymentLinksEnabled(data.payment_links_enabled === true);
      })
      .catch(() => {
        if (active) setPaymentLinksEnabled(false);
      })
      .finally(() => {
        if (active) setSettingsLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const today = formatApiDate(new Date());
    setMetricsLoading(true);

    Promise.all([
      collectionsEnabled
        ? getCollectionsDashboard()
            .then((data) => ({
              openBalance: data.kpis.total_open_debt,
              customers: (data.customers ?? []).slice(0, HOME_COLLECTION_CUSTOMER_LIMIT),
              available: true,
            }))
            .catch(() => ({
              openBalance: null,
              customers: [],
              available: false,
            }))
        : Promise.resolve({
            openBalance: null,
            customers: [],
            available: true,
          }),
      listInvoices({ from_date: today, to_date: today })
        .then((response) => (Array.isArray(response.data) ? response.data.length : null))
        .catch(() => null),
    ])
      .then(([collections, invoiceCount]) => {
        if (!active) return;
        setOpenBalance(collections.openBalance);
        setCollectionCustomers(collections.customers);
        setCollectionsAvailable(collections.available);
        setTodayInvoiceCount(invoiceCount);
      })
      .finally(() => {
        if (active) setMetricsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [collectionsEnabled]);

  const metrics = useMemo(() => {
    const items: { label: string; value: string; hint: string }[] = [];
    if (openBalance != null) {
      items.push({
        label: t('home.openBalance'),
        value: formatCurrency(openBalance),
        hint: t('home.openBalanceHint'),
      });
    }
    if (todayInvoiceCount != null) {
      items.push({
        label: t('home.todayInvoices'),
        value: String(todayInvoiceCount),
        hint: t('home.todayInvoicesHint'),
      });
    }
    return items;
  }, [openBalance, todayInvoiceCount, t]);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-gray-900">{t('home.title')}</h1>
        <p className="max-w-2xl text-sm text-gray-500">{t('home.subtitle')}</p>
      </header>

      <section className="grid gap-3 sm:grid-cols-3">
        <Link
          to="/create-document"
          className="flex min-h-28 items-center gap-4 rounded-md border border-primary-100 bg-primary-600 px-5 py-4 text-white shadow-sm transition-colors hover:bg-primary-700"
        >
          <FilePlus className="h-6 w-6 shrink-0" aria-hidden="true" />
          <span className="text-base font-semibold">{t('home.actions.newDocument')}</span>
        </Link>
        {settingsLoaded && paymentLinksEnabled && (
          <Link
            to="/create-payment"
            className="flex min-h-28 items-center gap-4 rounded-md border border-gray-200 bg-white px-5 py-4 text-gray-900 shadow-sm transition-colors hover:bg-gray-50"
          >
            <CreditCard className="h-6 w-6 shrink-0 text-primary-600" aria-hidden="true" />
            <span className="text-base font-semibold">{t('home.actions.paymentLink')}</span>
          </Link>
        )}
        <Link
          to="/customers"
          className="flex min-h-28 items-center gap-4 rounded-md border border-gray-200 bg-white px-5 py-4 text-gray-900 shadow-sm transition-colors hover:bg-gray-50"
        >
          <UserPlus className="h-6 w-6 shrink-0 text-primary-600" aria-hidden="true" />
          <span className="text-base font-semibold">{t('home.actions.newCustomer')}</span>
        </Link>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('home.shortcuts.title')}
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {shortcuts.map((shortcut) => {
            const Icon = shortcut.icon;
            return (
              <Link
                key={shortcut.to}
                to={shortcut.to}
                className="flex flex-col items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-5 text-center text-gray-900 shadow-sm transition-colors hover:bg-gray-50"
              >
                <Icon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                <span className="text-sm font-medium">{shortcut.label}</span>
              </Link>
            );
          })}
        </div>
      </section>

      <section>
        {metricsLoading ? (
          <div className="flex min-h-32 items-center justify-center rounded-md border border-gray-200 bg-white text-sm text-gray-500">
            <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
            {t('home.loading')}
          </div>
        ) : metrics.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-white p-8 text-sm text-gray-500">
            {t('home.noMetrics')}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {metrics.map((metric) => (
              <article key={metric.label} className="rounded-md border border-gray-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-medium text-gray-500">{metric.label}</div>
                <div className="mt-3 text-3xl font-semibold text-gray-900">{metric.value}</div>
                <div className="mt-2 text-sm text-gray-500">{metric.hint}</div>
              </article>
            ))}
          </div>
        )}
      </section>

      {collectionsEnabled && (
        <section
          data-testid="home-collections-card"
          className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
        >
          <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">{t('home.collectionsCard.title')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('home.collectionsCard.subtitle')}</p>
            </div>
            <Link
              to="/collections"
              className="shrink-0 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {t('home.collectionsCard.viewAll')}
            </Link>
          </div>

          {metricsLoading ? (
            <div className="flex min-h-28 items-center justify-center px-5 py-6 text-sm text-gray-500">
              <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
              {t('home.collectionsCard.loading')}
            </div>
          ) : !collectionsAvailable ? (
            <div className="px-5 py-8 text-sm text-gray-500">
              {t('home.collectionsCard.unavailable')}
            </div>
          ) : collectionCustomers.length === 0 ? (
            <div className="px-5 py-8 text-sm text-gray-500">
              {t('home.collectionsCard.empty')}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {collectionCustomers.map((customer) => (
                <article
                  key={customer.customer_id}
                  data-testid="home-collections-customer"
                  className="grid gap-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_repeat(3,minmax(7rem,auto))] sm:items-center"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">{customer.customer_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('home.collectionsCard.openBalance')}</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">
                      {formatCurrency(customer.open_balance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('home.collectionsCard.debtAge')}</div>
                    <div className="mt-1 text-sm font-medium text-gray-700">
                      {t('home.collectionsCard.days', { count: customer.oldest_unpaid_days })}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">{t('home.collectionsCard.priorityScore')}</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">{customer.collection_score}</div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
